import React, { useEffect, useState } from 'react';
import { Tabs, Table, Tooltip, Card, Row, Col, Statistic, Tag, Space } from 'antd';
import apiRaw from '../functions/api';
import api from '../functions/api';

const TrendsearchManagement = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ scripts: [], sources: [], companies: {}, trends: [], articles_per_source: [] });
  const [companyTrends, setCompanyTrends] = useState([]);
  const [sectorTrends, setSectorTrends] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/trendsearch_management_overview');
        setData(res.data || {});
        // Load extra tabs: Company Trends and Sector Trends
        const [companyRes, sectorRes] = await Promise.all([
          apiRaw.get('/trendsearch_all_data'),
          apiRaw.get('/trendsearch_sector_data')
        ]);
        if (companyRes?.data?.success) setCompanyTrends(companyRes.data.data || []);
        if (sectorRes?.data?.success) setSectorTrends(sectorRes.data.data || []);
      } catch (e) {
        console.error('Failed to load trendsearch management overview', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const scriptColumns = [
    { title: 'Script', dataIndex: 'script_name', key: 'script_name' },
    { title: 'Latest Start', dataIndex: 'latest_start_time', key: 'latest_start_time' },
    { title: 'Latest Success End', dataIndex: 'latest_success_end_time', key: 'latest_success_end_time' },
    {
      title: 'Essential Results',
      key: 'essential_results',
      render: (_, record) => {
        const text = record.essential_results;
        const short = text ? String(text).slice(0, 60) + (String(text).length > 60 ? '…' : '') : '—';
        return (
          <Tooltip title={<pre style={{ whiteSpace: 'pre-wrap', maxWidth: 600 }}>{String(text || '—')}</pre>}>
            <span>{short}</span>
          </Tooltip>
        );
      }
    }
  ];

  const sourcesColumns = [
    { title: 'Source', dataIndex: 'source_name', key: 'source_name' },
    { title: 'Domain', dataIndex: 'source_domain', key: 'source_domain' },
    { title: 'Articles', dataIndex: 'total_articles', key: 'total_articles' },
    { title: 'LLM Analyzed', dataIndex: 'analyzed_articles', key: 'analyzed_articles' },
    { title: 'Not Analyzed', dataIndex: 'not_analyzed_articles', key: 'not_analyzed_articles' },
  ];

  const trendColumns = [
    { title: 'Metric Type', dataIndex: 'metric_type', key: 'metric_type' },
    { title: 'Count', dataIndex: 'count', key: 'count' },
    { title: '%', dataIndex: 'pct', key: 'pct', render: (v) => (v != null ? `${v}%` : '—') },
  ];

  const articlesPerSourceColumns = [
    { title: 'Source', dataIndex: 'source_name', key: 'source_name' },
    {
      title: 'Avg Companies / Article',
      dataIndex: 'avg_companies_per_article',
      key: 'avg_companies_per_article',
      render: (v) => (v != null ? Number(v).toFixed(2) : '—')
    },
    {
      title: 'Impact Breakdown',
      key: 'impact_breakdown',
      render: (_, r) => {
        const b = r.impact_breakdown || {};
        const total = (b.winners || 0) + (b.losers || 0) + (b.enablers || 0) + (b.others || 0) + (b.none || 0);
        const pct = (n) => total > 0 ? ` (${((n / total) * 100).toFixed(0)}%)` : '';
        return (
          <Space size="small" wrap>
            <Tag color="green">W {b.winners || 0}{pct(b.winners || 0)}</Tag>
            <Tag color="red">L {b.losers || 0}{pct(b.losers || 0)}</Tag>
            <Tag color="blue">E {b.enablers || 0}{pct(b.enablers || 0)}</Tag>
            <Tag color="gold">O {b.others || 0}{pct(b.others || 0)}</Tag>
            <Tag>None {b.none || 0}{pct(b.none || 0)}</Tag>
          </Space>
        );
      }
    },
    {
      title: 'Avg Timeline (months) per Company',
      dataIndex: 'avg_timeline_months_per_company',
      key: 'avg_timeline_months_per_company',
      render: (v) => (v != null ? Number(v).toFixed(2) : '—')
    }
  ];

  const sentiment = data.companies?.sentiment_breakdown || {};

  return (
    <Tabs defaultActiveKey="scripts" items={[
      {
        key: 'scripts',
        label: 'Scripts',
        children: (
          <Table
            size="small"
            rowKey={(r) => r.script_name}
            columns={scriptColumns}
            dataSource={data.scripts || []}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )
      },
      {
        key: 'company_trends',
        label: 'Company Trends',
        children: (
          <Table
            size="small"
            rowKey={(r, i) => i}
            columns={[
              { title: 'Ticker', dataIndex: 'ticker', key: 'ticker', width: 90 },
              { title: 'Company', dataIndex: 'company_name', key: 'company_name', width: 180 },
              { title: 'Impact', dataIndex: 'impact_type', key: 'impact_type', width: 110 },
              { title: 'Metric', dataIndex: 'financial_metric', key: 'financial_metric', width: 120 },
              { title: 'Type', dataIndex: 'metric_type', key: 'metric_type', width: 100 },
              { title: 'Raw', dataIndex: 'raw_sentiment_score', key: 'raw', width: 70 },
              { title: 'Adj', dataIndex: 'sentiment_score', key: 'adj', width: 70 },
              { title: 'Final', dataIndex: 'final_sentiment_score', key: 'final', width: 80 },
              { title: 'Timeline', dataIndex: 'timeline_months', key: 'timeline', width: 90 },
              { title: 'Date', dataIndex: 'article_date', key: 'date', width: 110 },
              { title: 'Source', dataIndex: 'source_name', key: 'source', width: 120 }
            ]}
            dataSource={companyTrends}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )
      },
      {
        key: 'sector_trends',
        label: 'Sector Trends',
        children: (
          <Table
            size="small"
            rowKey={(r, i) => i}
            columns={[
              { title: 'Sector', dataIndex: 'sector', key: 'sector', width: 140 },
              { title: 'Industry', dataIndex: 'industry', key: 'industry', width: 200 },
              { title: 'Metric', dataIndex: 'financial_metric', key: 'financial_metric', width: 120 },
              { title: 'Type', dataIndex: 'metric_type', key: 'metric_type', width: 100 },
              { title: 'Raw', dataIndex: 'raw_sentiment_score', key: 'raw', width: 70 },
              { title: 'Adj', dataIndex: 'sentiment_score', key: 'adj', width: 70 },
              { title: 'Final', dataIndex: 'final_sentiment_score', key: 'final', width: 80 },
              { title: 'Timeline', dataIndex: 'timeline_months', key: 'timeline', width: 90 },
              { title: 'Date', dataIndex: 'article_date', key: 'date', width: 110 },
              { title: 'Source', dataIndex: 'source_name', key: 'source', width: 120 }
            ]}
            dataSource={sectorTrends}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )
      },
      {
        key: 'sources',
        label: 'Sources',
        children: (
          <Table
            size="small"
            rowKey={(r) => r.source_domain || r.source_name}
            columns={sourcesColumns}
            dataSource={data.sources || []}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )
      },
      {
        key: 'companies',
        label: 'Companies',
        children: (
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Card><Statistic title="Distinct Tickers" value={data.companies?.distinct_tickers || 0} /></Card>
            </Col>
            <Col xs={24} md={9}>
              <Card>
                <Row gutter={8}>
                  <Col span={12}><Statistic title="# Companies w/o Ticker" value={data.companies?.companies_without_ticker || 0} /></Col>
                  <Col span={12}><Statistic title="% Companies w/o Ticker" value={(data.companies?.companies_without_ticker_pct || 0)} suffix="%" precision={2} /></Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={9}>
              <Card title="Sentiment Breakdown">
                <Space size="small" wrap>
                  <Tag color="green">Positive {sentiment.positive?.count || 0} ({sentiment.positive?.pct || 0}%)</Tag>
                  <Tag color="red">Negative {sentiment.negative?.count || 0} ({sentiment.negative?.pct || 0}%)</Tag>
                  <Tag color="blue">Enabler {sentiment.enabler?.count || 0} ({sentiment.enabler?.pct || 0}%)</Tag>
                  <Tag>None {sentiment.none?.count || 0} ({sentiment.none?.pct || 0}%)</Tag>
                </Space>
              </Card>
            </Col>
          </Row>
        )
      },
      {
        key: 'trends',
        label: 'Trends',
        children: (
          <Table
            size="small"
            rowKey={(r) => r.metric_type}
            columns={trendColumns}
            dataSource={data.trends || []}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )
      },
      {
        key: 'articles',
        label: 'Articles per Source',
        children: (
          <Table
            size="small"
            rowKey={(r) => r.source_name}
            columns={articlesPerSourceColumns}
            dataSource={data.articles_per_source || []}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )
      }
    ]} />
  );
};

export default TrendsearchManagement;


